import React, { useState } from 'react'
import { withRouter } from 'react-router-dom'
import { withTranslation } from 'react-i18next'
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import { useNotifications } from '../../context/Notifications'
import { playNewOrderSound } from '../../utils/orderAlertSound'
import { IconTile } from '../UI/Depth'
import depth, { cardSx, toneTile } from '../../utils/depth'

const ICONS = {
  NEW_ORDER: ReceiptLongIcon,
  STATUS: AutorenewIcon,
  ACCEPTED: CheckCircleOutlineIcon,
  REJECTED: HighlightOffIcon,
  DELIVERED: TaskAltIcon,
  RIDER: TwoWheelerIcon,
  PICKUP: TwoWheelerIcon,
  COURIER: LocalShippingIcon
}

const TONES = {
  NEW_ORDER: 'orange',
  ACCEPTED: 'green',
  DELIVERED: 'green',
  REJECTED: 'rose',
  RIDER: 'violet',
  PICKUP: 'blue',
  COURIER: 'blue',
  STATUS: 'slate'
}

const timeAgo = (iso, t) => {
  const diff = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(diff)) return ''
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return t('JustNow')
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

function NotificationBell(props) {
  const { t, history } = props
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState(null)
  const {
    notifications,
    unreadCount,
    enabled,
    scopeType,
    markAsRead,
    markAllAsRead,
    clearAll,
    soundMuted,
    toggleSound
  } = useNotifications()

  const onToggleSound = () => {
    // Play a sample when turning it on, so staff can check their speakers.
    if (soundMuted) playNewOrderSound({ force: true })
    toggleSound()
  }

  const open = Boolean(anchorEl)

  const onSelect = notification => {
    markAsRead(notification.id)
    setAnchorEl(null)
    history.push(
      scopeType === 'SUPER_ADMIN' ? '/super_admin/orders' : '/admin/orders'
    )
  }

  return (
    <>
      <IconButton
        size="medium"
        aria-label="notifications"
        onClick={event => setAnchorEl(event.currentTarget)}
        sx={{
          bgcolor: 'common.white',
          border: '1px solid',
          borderColor: 'divider',
          width: 40,
          height: 40,
          '&:hover': { bgcolor: 'background.primary' }
        }}>
        <Badge
          overlap="circular"
          badgeContent={unreadCount}
          max={99}
          invisible={unreadCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              boxShadow: `0 0 0 2px ${theme.palette.common.white}`,
              fontSize: 10,
              height: 16,
              minWidth: 16
            }
          }}>
          <NotificationsNoneIcon
            fontSize="small"
            sx={{ color: 'text.secondary' }}
          />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            ...cardSx,
            width: 400,
            maxWidth: '95vw',
            mt: 1,
            overflow: 'hidden'
          }
        }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: depth.headerBackground
          }}>
          <IconTile icon={NotificationsNoneIcon} size={36} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {t('Notifications')}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
              {scopeType === 'SUPER_ADMIN'
                ? t('AllMarketplaceActivity')
                : t('ActivityForThisStore')}
            </Typography>
          </Box>
          <Tooltip
            title={soundMuted ? t('NewOrderSoundOff') : t('NewOrderSoundOn')}>
            <IconButton
              size="small"
              aria-label={
                soundMuted ? t('NewOrderSoundOff') : t('NewOrderSoundOn')
              }
              onClick={onToggleSound}
              sx={{ color: soundMuted ? 'text.disabled' : 'primary.main' }}>
              {soundMuted ? (
                <VolumeOffIcon fontSize="small" />
              ) : (
                <VolumeUpIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {notifications.length > 0 ? (
            <Button
              size="small"
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
              sx={{ fontSize: 11, textTransform: 'none' }}>
              {t('MarkAllAsRead')}
            </Button>
          ) : null}
        </Box>
        <Divider />

        {!enabled ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {t('SelectStoreToSeeNotifications')}
            </Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 36, color: 'grey.300' }} />
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1 }}>
              {t('NoNotificationsYet')}
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ maxHeight: 420, overflowY: 'auto', py: 0 }}>
              {notifications.map(notification => {
                const Icon = ICONS[notification.type] || ReceiptLongIcon
                return (
                  <ListItemButton
                    key={notification.id}
                    onClick={() => onSelect(notification)}
                    sx={{
                      alignItems: 'flex-start',
                      gap: 1.5,
                      py: 1.25,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: notification.read ? 'transparent' : '#FFF8F3'
                    }}>
                    <Box
                      sx={{
                        mt: 0.25,
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        ...toneTile(TONES[notification.type] || 'orange')
                      }}>
                      <Icon sx={{ fontSize: 17, color: 'common.white' }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 1
                        }}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: notification.read ? 500 : 700,
                            color: 'text.primary'
                          }}>
                          {notification.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: 'text.disabled',
                            whiteSpace: 'nowrap'
                          }}>
                          {timeAgo(notification.createdAt, t)}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {notification.body}
                      </Typography>
                    </Box>
                  </ListItemButton>
                )
              })}
            </List>
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button
                size="small"
                onClick={clearAll}
                sx={{
                  fontSize: 11,
                  textTransform: 'none',
                  color: 'text.disabled'
                }}>
                {t('ClearAll')}
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  )
}

export default withRouter(withTranslation()(NotificationBell))
